/**
 * @file themedAsset.ts
 * @description
 * -----------------------------------------------------
 * Utilidad para crear assets temáticos con variantes light/dark
 * -----------------------------------------------------
 * @version 1.0.0
 * @created 2025-12-10
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

const ASSETS_PATH = "/assets";

/**
 * Representa un activo con variantes para temas claro y oscuro.
 */
export interface ThemedAsset {
  /** Ruta del activo para tema claro */
  light: string;
  /** Ruta del activo para tema oscuro */
  dark: string;
}

/**
 * Opciones para crear un activo temático.
 */
export interface ThemedAssetOptions {
  /** Ruta de la carpeta donde se encuentra el activo */
  path: "brands" | "img" | "svg";
  /** Nombre del archivo sin extensión */
  filename: string;
  /** Extensión del archivo (por defecto "png") */
  extension?: string;
  /** Indica si existe una variante oscura del activo (por defecto true) */
  hasDarkVariant?: boolean;
}

/**
 * Crea un objeto ThemedAsset con rutas para variantes de tema claro y oscuro.
 *
 * @param options - Opciones de configuración del activo
 * @returns Objeto con las rutas light y dark del activo
 *
 * @example
 * ```typescript
 * // Con variante oscura
 * themedAsset({ path: "img", filename: "logo" })
 * // => { light: "/assets/img/logo.png", dark: "/assets/img/logo-dark.png" }
 *
 * // Sin variante oscura
 * themedAsset({ path: "img", filename: "logo", hasDarkVariant: false })
 * // => { light: "/assets/img/logo.png", dark: "/assets/img/logo.png" }
 * ```
 */
export const themedAsset = ({
  path,
  filename,
  extension = "png",
  hasDarkVariant = true,
}: ThemedAssetOptions): ThemedAsset => {
  const lightPath = `${ASSETS_PATH}/${path}/${filename}.${extension}`;
  const darkPath = hasDarkVariant
    ? `${ASSETS_PATH}/${path}/${filename}-dark.${extension}`
    : lightPath;

  return {
    light: lightPath,
    dark: darkPath,
  };
};
